import React, { Component } from 'react';
import PropTypes from 'prop-types';
import autoBind from 'react-autobind';
import { injectIntl, defineMessages } from 'react-intl';

import Icon from 'ui/common/icon';
import Skill from 'ui/common/rebranding/components/Skill';
import { skillLevelIds, skillLevels, skillLevelIdsOrder } from 'resources/skills/levels';

import classnames from 'classnames';

const messages = defineMessages({
  add: {
    id: 'users.skills.add-button.title',
    defaultMessage: 'Add New Skill',
  },
  validationCountsBySkillLevelIdCount: {
    id: 'users.skills.validationCountsBySkillLevelIdCount',
    defaultMessage: `{count, number} {levelName, select,
      Beginner {beginner}
      Intermediate {intermediate}
      Expert {expert}
    } {count, plural,
      one {validation}
      other {validations}
    }`,
  },
});

export class LevelIncrementAndDecrement extends Component {
  static propTypes = {
    updateSkillLevel: PropTypes.func,
    entry: PropTypes.shape({
      skill: PropTypes.shape({
        name: PropTypes.string.isRequired,
        category: PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          className: PropTypes.string.isRequired,
        }),
      }),
      level: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
      validations: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          type: PropTypes.string,
          level: PropTypes.string.isRequired,
        })
      ),
      validationCountsBySkillLevelIds: PropTypes.shape({
        [skillLevelIds.beginner]: PropTypes.number,
        [skillLevelIds.intermediate]: PropTypes.number,
        [skillLevelIds.expert]: PropTypes.number,
      }),
      isValidatedAtCurrentLevel: PropTypes.bool,
    }),
    onUpdate: PropTypes.func,
  };

  constructor(props) {
    super(props);
    autoBind(this);

    this.state = {
      currentLevelPower: null,
      disabledDecreaseLevel: false,
      disabledIncreaseLevel: false,
      validatedAtCurrentLevel: null,
    };
  }

  componentDidMount() {
    const { entry } = this.props;

    if (!entry) return null;

    if (entry.level.power === 3 && entry.isValidatedAtCurrentLevel) {
      this.setState({
        disabledIncreaseLevel: true,
        disabledDecreaseLevel: true,
        validatedAtCurrentLevel: entry.isValidatedAtCurrentLevel,
        currentLevelPower: entry.level.power,
      });
    } else if (entry.level.power && entry.isValidatedAtCurrentLevel) {
      this.setState({
        disabledDecreaseLevel: true,
        validatedAtCurrentLevel: entry.isValidatedAtCurrentLevel,
        currentLevelPower: entry.level.power,
      });
    } else if (!entry.isValidatedAtCurrentLevel && entry.level.power >= 3) {
      this.setState({
        disabledDecreaseLevel: false,
        disabledIncreaseLevel: true,
        validatedAtCurrentLevel: entry.isValidatedAtCurrentLevel,
        currentLevelPower: entry.level.power,
      });
    } else if (!entry.isValidatedAtCurrentLevel && entry.level.power > 1) {
      this.setState({
        disabledDecreaseLevel: false,
        disabledIncreaseLevel: false,
        validatedAtCurrentLevel: entry.isValidatedAtCurrentLevel,
        currentLevelPower: entry.level.power,
      });
    } else if (!entry.isValidatedAtCurrentLevel && entry.level.power === 1) {
      this.setState({
        disabledDecreaseLevel: true,
        disabledIncreaseLevel: false,
        validatedAtCurrentLevel: entry.isValidatedAtCurrentLevel,
        currentLevelPower: entry.level.power,
      });
    } else {
      this.setState({
        disabledDecreaseLevel: true,
        validatedAtCurrentLevel: entry.isValidatedAtCurrentLevel,
        currentLevelPower: entry.level.power + 1,
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { updateSkillLevel } = this.props;
    const indexOfLevel = this.state.currentLevelPower - 1;
    const currentLevel = skillLevelIdsOrder[indexOfLevel];

    if (updateSkillLevel && prevState.currentLevelPower !== this.state.currentLevelPower) {
      updateSkillLevel({
        currentLevel,
      });
    }
  }

  checkValidatedAtCurrentLevel(entry, type) {
    const { currentLevelPower } = this.state;
    const { level, isValidatedAtCurrentLevel } = entry;

    if (level && level.power === currentLevelPower && isValidatedAtCurrentLevel) {
      this.setState({
        validatedAtCurrentLevel: isValidatedAtCurrentLevel,
      });
    } else if (
      level &&
      level.power === currentLevelPower + 1 &&
      type === 'increment' &&
      isValidatedAtCurrentLevel
    ) {
      this.setState({
        validatedAtCurrentLevel: isValidatedAtCurrentLevel,
        disabledDecreaseLevel: true,
      });
    } else if (
      level.power === currentLevelPower - 1 &&
      type === 'decrement' &&
      isValidatedAtCurrentLevel
    ) {
      this.setState({
        validatedAtCurrentLevel: isValidatedAtCurrentLevel,
        disabledDecreaseLevel: true,
      });
    } else if (
      level.power === currentLevelPower - 2 &&
      type === 'decrement' &&
      isValidatedAtCurrentLevel
    ) {
      this.setState({
        disabledDecreaseLevel: true,
      });
    } else {
      this.setState({
        validatedAtCurrentLevel: false,
        disabledDecreaseLevel: false,
      });
    }
  }

  decreaseSkillLevel() {
    const { currentLevelPower, validatedAtCurrentLevel } = this.state;
    const { entry } = this.props;
    const type = 'decrement';

    this.checkValidatedAtCurrentLevel(entry, type);

    if (validatedAtCurrentLevel) {
      this.setState({
        disabledDecreaseLevel: true,
      });
    } else if (currentLevelPower <= 1) {
      this.setState({
        disabledIncreaseLevel: false,
      });
    } else if (currentLevelPower - 1 <= 1) {
      this.setState({
        currentLevelPower: this.state.currentLevelPower - 1,
        disabledIncreaseLevel: false,
        disabledDecreaseLevel: true,
      });
    } else {
      this.setState({
        currentLevelPower: this.state.currentLevelPower - 1,
        disabledIncreaseLevel: false,
      });
    }
  }

  increaseSkillLevel() {
    const { currentLevelPower, validatedAtCurrentLevel } = this.state;
    const { entry } = this.props;
    const type = 'increment';

    this.checkValidatedAtCurrentLevel(entry, type);

    if (currentLevelPower >= 3) {
      this.setState({
        disabledIncreaseLevel: true,
      });
    } else if (currentLevelPower + 1 >= 3) {
      this.setState({
        currentLevelPower: currentLevelPower + 1,
        disabledIncreaseLevel: true,
        validatedAtCurrentLevel: false,
      });
    } else if (validatedAtCurrentLevel) {
      this.setState({
        currentLevelPower: currentLevelPower + 1,
        disabledIncreaseLevel: false,
        disabledDecreaseLevel: true,
        validatedAtCurrentLevel: false,
      });
    } else {
      this.setState({
        currentLevelPower: currentLevelPower + 1,
        disabledIncreaseLevel: false,
        disabledDecreaseLevel: false,
      });
    }
  }

  renderSkill(entry) {
    const { intl: { formatMessage } } = this.props;
    const { currentLevelPower } = this.state;
    const { disabledDecreaseLevel, disabledIncreaseLevel } = this.state;

    return (
      <Skill
        id={`user_skill_${entry.id}`}
        skillLevel={currentLevelPower}
        validated={entry.isValidatedAtCurrentLevel}
        categoryClasses={entry.skill.category ? `${entry.skill.category.className} gradient` : ''}
      >
        <h3>{entry && entry.skill && entry.skill.name}</h3>

        {Object.keys(entry.validationCountsBySkillLevelIds)
          .map(skillLevelId => {
            const level = skillLevels[skillLevelId];
            return entry.validationCountsBySkillLevelIds[skillLevelId] ? (
              <div ref="label" key={skillLevelId}>
                <small>
                  {formatMessage(messages.validationCountsBySkillLevelIdCount, {
                    count: entry.validationCountsBySkillLevelIds[skillLevelId].toString(),
                    levelName: level.name,
                  })}
                </small>
              </div>
            ) : null;
          })
          .filter(Boolean)}

        <div ref="decrement">
          <Icon
            disabled={disabledDecreaseLevel}
            background
            name="icon--arrow-down"
            tooltip="Decrease Skill Level"
            onClick={this.decreaseSkillLevel}
          />
        </div>

        <div ref="increment">
          <Icon
            disabled={disabledIncreaseLevel}
            background
            name="icon--arrow-up"
            tooltip="Increase Skill Level"
            onClick={this.increaseSkillLevel}
          />
        </div>
      </Skill>
    );
  }

  render() {
    const { className, entry } = this.props;

    if (!entry) return null;
    if (Object.keys(entry).length === 0) return null;

    return (
      <div className={classnames(className, 'd-flex justify-content-center')}>
        <div className={classnames('pl-3 mr-3')}>{this.renderSkill(entry)}</div>
      </div>
    );
  }
}

export default injectIntl(LevelIncrementAndDecrement);
